import {
  ApplicationConfig,
  ErrorHandler,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  inject,
  isDevMode,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withNavigationErrorHandler,
  withRouterConfig,
} from '@angular/router';

import { routes } from './app.routes';
import { provideServiceWorker } from '@angular/service-worker';
import { provideGraphQL } from './core/graphql.providers';
import { provideAppTransloco } from './core/services/transloco.providers';
import { provideCdnImageLoader } from './core/utils/cdn-image-loader';
import { chunkReloadNavigationError } from './core/utils/chunk-reload';
import { AnalyticsErrorHandler } from './core/services/analytics-error-handler';
import { ThemeService } from './core/services/theme.service';
import { DivisionBrandingService } from './core/services/division-branding.service';
import { providePaperTranslator } from './core/services/paper-bridge.providers';
import { paperShellProviders } from './shell/paper-shell.providers';
import { RICOH_MAPS_CONFIG } from '@ricoh-maps/angular/core';
import { environment } from '../environments/environment';
import { provideSettingsAsk } from './features/settings-ask/settings-ask.providers';
import { TranslocoService } from '@jsverse/transloco';
import { DEFAULT_COOKIE_POLICY_URL } from '@ricohproducts/consent';
import { provideConsent, provideConsentLanguageSync } from '@ricohproducts/consent/angular';
import { isVisitorPagePath } from './features/visitors/visitor-form/visitor-page-link';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // The app's ErrorHandler: logs, reloads once for a stale lazy chunk (a tab
    // older than the last deploy), and captures everything else for PostHog.
    // Ported from wip-map with the class but never registered here, so the
    // stale-chunk recovery inside it had never run (core/utils/chunk-reload.ts).
    { provide: ErrorHandler, useClass: AnalyticsErrorHandler },
    provideZoneChangeDetection({ eventCoalescing: true }),
    // `withComponentInputBinding()` binds route params/data/query to matching
    // component `input()`s. REQUIRED: many detail pages take their id from the
    // route this way (e.g. capability-detail `key`, space-detail `id`, policies,
    // discover, content-studio, videos). Without it those inputs stay at their
    // default '', the load effect returns early, and the page renders blank with
    // no skeleton or error. Mirrors wip-map's app.config.
    //
    // `withNavigationErrorHandler`: a route whose chunk 404s after a deploy
    // notes where the person was going and hands the error to the ErrorHandler,
    // so its one recovery reload lands on that page, not the one they left.
    provideRouter(
      routes,
      withComponentInputBinding(),
      withNavigationErrorHandler(chunkReloadNavigationError),
      // Pinned to today's default: Angular 22 switches it to 'always'.
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }),
    ),
    provideGraphQL(),
    // Runtime i18n (Transloco). Loads /i18n/<lang>.json at runtime — chosen
    // over compile-time $localize for ~22-language support (one build, runtime
    // language switch, add-a-language-is-add-a-file). Mirrors wip-map.
    ...provideAppTransloco(),
    // The shared cookie consent banner (@ricohproducts/consent), one choice
    // across login, the employee app and Settings. After Transloco so the
    // banner follows the active language, which LocaleService sets from the
    // signed-in person's profile. Inside the Teams iframe the package keeps
    // the choice in localStorage (`isEmbedded`). Behind CONSENT_ENABLED: off,
    // the banner never starts and PostHog runs as it did before it. Nor on
    // the guest visitor page, which runs no analytics and asks nothing.
    provideConsent({
      startOn:
        environment.CONSENT_ENABLED && !isVisitorPagePath(window.location.pathname)
          ? 'init'
          : 'manual',
      policies: {
        cookies: environment.COOKIE_POLICY_URL || DEFAULT_COOKIE_POLICY_URL,
        privacy: environment.PRIVACY_POLICY_URL || undefined,
      },
    }),
    provideConsentLanguageSync(TranslocoService),
    // Paper's own copy through Transloco (with Paper's en/ja behind it), and
    // the seams Paper's workspace shell reads: the theme, the accent's
    // storage (the synced `brandColorOverride`), the side nav's fold and ⌘K's
    // Recent (One Paper A4).
    providePaperTranslator(),
    ...paperShellProviders,
    // Ask in Settings: gemba's admin agent on Paper's Ask kit — the one
    // conversation, the confirm card, links to admin records, the push panel
    // and the doors (the "Ask Admin" chip, ⌘J, the palette).
    ...provideSettingsAsk(),
    // NgOptimizedImage loader — resolves `ngSrc` values (e.g. location photos)
    // against CDN_BASE_URL for relative paths; absolute URLs pass through.
    provideCdnImageLoader(),
    // Construct the appearance services at boot so their side-effects run before
    // first paint: ThemeService writes [data-paper-theme]/[data-paper-font] on
    // <html>, and DivisionBrandingService hands the division (cached from the
    // last visit until the scope answers) to PaperAccentService, which it
    // constructs, so the person's accent paints from the first frame. Both are
    // providedIn:'root'; injecting them here forces the eager construction
    // their DOM side-effects depend on.
    provideAppInitializer(() => {
      inject(ThemeService);
      inject(DivisionBrandingService);
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // Ricoh Maps engine PORTS (bundle source, availability, …) are deliberately
    // NOT registered here — they stay route-scoped to the map surfaces via
    // provideCampusMapEngine() (features/estate/campuses/map-engine.providers.ts)
    // so the engine loads only when a map is opened.
    //
    // RICOH_MAPS_CONFIG is the one exception, and it has to be root. The engine's
    // root MapDataService seeds itself in its constructor from
    // `localStorage['mapbox_token'] || config.mapboxToken` and forwards that to
    // the root MapTokenProvider — which is what every map surface actually gates
    // on. Scope the config to a route and MapDataService may be constructed
    // before that route is ever entered, seed itself with '', and leave every
    // map dead. wip-map provides exactly this at root (app.config.ts) after
    // hitting the same bug; this port dropped it. Imported from the light
    // '/core' entry, so no Mapbox GL / Three.js in the initial chunk.
    { provide: RICOH_MAPS_CONFIG, useValue: { mapboxToken: environment.MAPBOX_KEY, exposeMapForTesting: !environment.production } },
  ],
};
